import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, Loader2, RefreshCw } from "lucide-react";

export default function EmailPreview() {
  const [types, setTypes] = useState([]);
  const [meta, setMeta] = useState({});
  const [sel, setSel] = useState("household_welcome");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/email/types").then(({ data }) => { setTypes(data.types || []); setMeta(data); }).catch(() => {});
  }, []);

  const load = useCallback(async (t) => {
    setLoading(true);
    try { const { data } = await api.get("/admin/email/preview", { params: { type: t } }); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load preview"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(sel); }, [sel, load]);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/send-test", { type: sel });
      if (data.sent) toast.success(`Test sent to ${data.to}`);
      else toast.info(`Captured in ${data.mode} mode (not delivered)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send test"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Platform admin" title="Email preview"
        subtitle="See exactly what a transactional email looks like before it goes out, and send yourself a test." />
      <div className="flex flex-wrap gap-2 items-center" data-testid="email-type-tabs">
        {types.map((t) => (
          <button key={t.id} onClick={() => setSel(t.id)} data-testid={`email-type-${t.id}`}
            className={`text-sm rounded-full px-4 py-1.5 font-semibold border ${sel === t.id ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-[#0B192C] border-[#D7D4CB]"}`}>{t.label}</button>
        ))}
        <Button variant="outline" onClick={() => load(sel)} className="ml-auto h-9" data-testid="email-refresh"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
        <Button onClick={sendTest} disabled={sending} className="h-9 bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620]" data-testid="email-send-test">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send test to myself</>}
        </Button>
      </div>
      <p className="text-xs text-[#6B7280]" data-testid="email-sender-meta">
        From name <strong>{meta.from_name || "Nauticus"}</strong> · Reply-To <strong>{meta.reply_to || "—"}</strong> · mode <strong>{meta.mode || "—"}</strong>
      </p>
      <div className="rounded-xl border-2 border-[#E5E5E0] bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b bg-[#FBFBF8] text-sm"><span className="text-[#8A8F98]">Subject:</span> <span className="font-semibold text-[#0B192C]" data-testid="email-subject">{preview?.subject || "—"}</span></div>
        {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#1E3E62]" /></div> :
          <iframe title="email-preview" data-testid="email-iframe" srcDoc={preview?.html || ""} className="w-full bg-white" style={{ height: "60vh", border: 0 }} />}
      </div>
    </div>
  );
}
